import { useMemo } from "react"

import { useAgentNamesTransport } from "@/agents/transport"
import {
  buildMetricIdentityThresholdSourcesById,
  useMetricsList,
  type MetricIdentityThresholdSource,
  type MetricThresholdSource,
} from "@/metrics/catalog"

import type { QueryDashboardBatchRequest } from "@/types_api/backend/transport"
import type { TargetCondition } from "@/types/metrics/contracts"
import {
  calculateDynamicDateRange,
  calculatePresetDateRange,
} from "@/utils/date/dateRange"

import type { MetricWidgetConfig } from "../components/BaseWidget"
import { useDashboardFilters } from "../contexts/DashboardFiltersContext"
import { useDashboardBatchTransportQuery } from "../routes/dashboardBatchTransportQuery"
import { GROUP_BY_TYPE, type GroupByType } from "../types/chart-types"
import {
  AGGREGATION_TYPE,
  type FilterType,
  type MetricFilter,
} from "../types/core"
import { DEFAULT_WIDGET_DATE_RANGE } from "../types/dateRange"
import { createMetadataGroupKey } from "../utils/chart/splitKeys"
import type { MetricValue } from "../utils/chartShared"
import {
  isHumanReviewSource,
  isUploadedConversationSource,
  resolveDataSource,
} from "../utils/dataSource"
import { reconcileTableMetadataCohorts } from "../utils/tableMetadataCohorts"
import {
  partitionMetricIdsByOutputType,
  resolveTableMetricIds,
} from "../utils/tableWidgetData"
import { getBrowserTimezone } from "../utils/timeBucketing"
import {
  isTransportDashboardQueryLoading,
  serializeDashboardBatchQuery,
} from "../utils/transportDashboardQueries"

interface UseTableWidgetDataParams {
  config: MetricWidgetConfig & {
    type: "table"
    groupBy?: GroupByType
    groupByMetadataKey?: string
    filters?: FilterType
    metricFilter?: MetricFilter[]
  }
}

interface TableWidgetDataResult {
  dateRange: { end: Date; start: Date }
  error: string | null
  hasMetrics: boolean
  isGlobalOverrideActive: boolean
  isLoading: boolean
  metricRecords: Record<string, MetricIdentityThresholdSource>
  metricValues: MetricValue[]
}

const CHART_QUERY_GC_TIME = 15 * 60 * 1000
const CHART_QUERY_STALE_TIME = 5 * 60 * 1000

const buildTargetConditionsByMetricId = ({
  metricIds,
  metricRecords,
}: {
  metricIds: string[]
  metricRecords: Record<string, MetricThresholdSource>
}) => {
  if (metricIds.length === 0) {
    return undefined
  }

  const targetConditionsByMetricId = metricIds.reduce<
    Record<string, TargetCondition>
  >((acc, metricId) => {
    const targetCondition =
      metricRecords[metricId]?.metric_metadata?.target_condition
    if (targetCondition) {
      acc[metricId] = targetCondition
    }
    return acc
  }, {})

  return Object.keys(targetConditionsByMetricId).length > 0
    ? targetConditionsByMetricId
    : undefined
}

const normalizeTableMetricValues = ({
  agentNames,
  groupByMetadataKey,
  rows,
}: {
  agentNames?: Record<string, string>
  groupByMetadataKey?: string
  rows: Record<string, unknown>[]
}): MetricValue[] =>
  rows.map((row) => ({
    // Table rows share the broader MetricValue contract, which expects a number.
    // Empty cells are represented by missing rows/cells rather than a null row.value.
    value: row.value != null ? Number(row.value) : 0,
    count: row.count != null ? Number(row.count) : undefined,
    createdAt:
      typeof row.run_occurred_at === "string"
        ? row.run_occurred_at
        : new Date(0).toISOString(),
    metricId:
      typeof row.metric_id === "string"
        ? row.metric_id
        : typeof row.metricId === "string"
          ? row.metricId
          : "",
    metric_id:
      typeof row.metric_id === "string"
        ? row.metric_id
        : typeof row.metricId === "string"
          ? row.metricId
          : undefined,
    agentName:
      typeof row.run_agent_id === "string"
        ? agentNames?.[row.run_agent_id] || row.run_agent_id
        : undefined,
    assignee: typeof row.assignee === "string" ? row.assignee : undefined,
    metadataValue:
      groupByMetadataKey || typeof row.metadata_value === "string"
        ? createMetadataGroupKey({
            kind:
              row.metadata_group_kind === "remainder" ? "remainder" : "value",
            metadataKey: groupByMetadataKey,
            topValues: Array.isArray(row.metadata_top_values)
              ? row.metadata_top_values.filter(
                  (value): value is string => typeof value === "string",
                )
              : undefined,
            value:
              typeof row.metadata_value === "string"
                ? row.metadata_value
                : null,
          })
        : undefined,
    mutation_id:
      typeof row.mutation_id === "string" ? row.mutation_id : undefined,
    mutationName:
      typeof row.mutation_name === "string"
        ? row.mutation_name
        : typeof row.mutationName === "string"
          ? row.mutationName
          : undefined,
    personaName:
      typeof row.persona_name === "string"
        ? row.persona_name
        : typeof row.personaName === "string"
          ? row.personaName
          : undefined,
    persona_id: typeof row.persona_id === "string" ? row.persona_id : undefined,
    run_agent_id:
      typeof row.run_agent_id === "string" ? row.run_agent_id : undefined,
    run_template_name:
      typeof row.run_template_name === "string"
        ? row.run_template_name
        : undefined,
    templateName:
      typeof row.template_name === "string"
        ? row.template_name
        : typeof row.templateName === "string"
          ? row.templateName
          : undefined,
    simulationOutputCreatedAt:
      typeof row.run_occurred_at === "string" ? row.run_occurred_at : undefined,
    testSetName:
      typeof row.test_set_name === "string"
        ? row.test_set_name
        : typeof row.testSetName === "string"
          ? row.testSetName
          : undefined,
    test_set_id:
      typeof row.test_set_id === "string" ? row.test_set_id : undefined,
  }))

export const useTableWidgetData = ({
  config,
}: UseTableWidgetDataParams): TableWidgetDataResult => {
  const { state } = useDashboardFilters()
  const {
    dateRange: globalDateRange,
    globalDateRangeOverride,
    isOverrideActive,
  } = state

  const effectiveDateRange = useMemo(() => {
    if (isOverrideActive && globalDateRangeOverride) {
      return globalDateRangeOverride
    }
    if (globalDateRange) {
      return globalDateRange
    }

    if (config.dateRange) {
      if (config.dateRange.preset) {
        return calculatePresetDateRange(config.dateRange.preset)
      }

      if (config.dateRange.relativeConfig) {
        return calculateDynamicDateRange(
          config.dateRange.relativeConfig.amount,
          config.dateRange.relativeConfig.unit,
        )
      }

      if (config.dateRange.customRange) {
        return config.dateRange.customRange
      }
    }

    return calculatePresetDateRange(DEFAULT_WIDGET_DATE_RANGE.preset!)
  }, [
    config.dateRange,
    globalDateRange,
    globalDateRangeOverride,
    isOverrideActive,
  ])

  const effectiveMetricIds = useMemo(() => {
    return resolveTableMetricIds({
      filters: config.filters,
      metricId: config.metricId,
      metricIds: config.metricIds,
    })
  }, [config.filters, config.metricId, config.metricIds])

  const hasMetrics = effectiveMetricIds.length > 0

  const { data: allOrgMetricRecords, isLoading: isMetricRecordsLoading } =
    useMetricsList({
      enabled: hasMetrics,
    })

  const metricRecords = useMemo(() => {
    if (!allOrgMetricRecords) {
      return {}
    }

    const recordsById =
      buildMetricIdentityThresholdSourcesById(allOrgMetricRecords)

    return Object.fromEntries(
      effectiveMetricIds
        .map((metricId) => [metricId, recordsById[metricId]] as const)
        .filter((entry): entry is [string, MetricIdentityThresholdSource] =>
          Boolean(entry[1]),
        ),
    )
  }, [allOrgMetricRecords, effectiveMetricIds])

  const partitions = useMemo(
    () =>
      partitionMetricIdsByOutputType({
        metricIds: effectiveMetricIds,
        metricRecords,
      }),
    [effectiveMetricIds, metricRecords],
  )

  const floatMetricIds = useMemo(
    () =>
      partitions.find((partition) => partition.outputType === "float")
        ?.metricIds ?? [],
    [partitions],
  )
  const stringMetricIds = useMemo(
    () =>
      partitions.find((partition) => partition.outputType === "string")
        ?.metricIds ?? [],
    [partitions],
  )

  const dataSource = resolveDataSource(config)
  const isMonitoring = isUploadedConversationSource(dataSource)
  const isHumanReview = isHumanReviewSource(dataSource)
  const timezone = getBrowserTimezone()
  const hasFloatMetrics = hasMetrics && floatMetricIds.length > 0
  const hasStringMetrics = hasMetrics && stringMetricIds.length > 0
  const isFloatDashboardQueryEnabled = hasFloatMetrics && !isHumanReview
  const isStringDashboardQueryEnabled = hasStringMetrics && !isHumanReview
  const isFloatHumanReviewQueryEnabled = hasFloatMetrics && isHumanReview
  const isStringHumanReviewQueryEnabled = hasStringMetrics && isHumanReview

  const dashboardBatchRequest = useMemo(() => {
    const queries: QueryDashboardBatchRequest["queries"] = []

    if (isFloatDashboardQueryEnabled) {
      queries.push(
        serializeDashboardBatchQuery({
          query_id: "float_dashboard",
          kind: "queryDashboardMetric",
          type: "table",
          metricOutputType: "float",
          aggregation: config.aggregation || AGGREGATION_TYPE.SUCCESS,
          monitoring: isMonitoring,
          startDate: effectiveDateRange.start.toISOString(),
          endDate: effectiveDateRange.end.toISOString(),
          filters: {
            ...config.filters,
            metricIds: floatMetricIds,
          },
          groupBy:
            config.groupBy === GROUP_BY_TYPE.ASSIGNEE
              ? undefined
              : config.groupBy,
          groupByMetadataKey: config.groupByMetadataKey,
          timezone,
          metricFilter: config.metricFilter,
          targetConditionsByMetricId: buildTargetConditionsByMetricId({
            metricIds: floatMetricIds,
            metricRecords,
          }),
        }),
      )
    }

    if (isStringDashboardQueryEnabled) {
      queries.push(
        serializeDashboardBatchQuery({
          query_id: "string_dashboard",
          kind: "queryDashboardMetric",
          type: "table",
          metricOutputType: "string",
          aggregation: config.aggregation || AGGREGATION_TYPE.SUCCESS,
          monitoring: isMonitoring,
          startDate: effectiveDateRange.start.toISOString(),
          endDate: effectiveDateRange.end.toISOString(),
          filters: {
            ...config.filters,
            metricIds: stringMetricIds,
          },
          groupBy:
            config.groupBy === GROUP_BY_TYPE.ASSIGNEE
              ? undefined
              : config.groupBy,
          groupByMetadataKey: config.groupByMetadataKey,
          timezone,
          metricFilter: config.metricFilter,
          targetConditionsByMetricId: buildTargetConditionsByMetricId({
            metricIds: stringMetricIds,
            metricRecords,
          }),
        }),
      )
    }

    if (isFloatHumanReviewQueryEnabled) {
      queries.push(
        serializeDashboardBatchQuery({
          query_id: "float_human_review",
          kind: "queryHumanReviewMetric",
          type: "table",
          metricIds: floatMetricIds,
          metricOutputType: "float",
          aggregation: config.aggregation || AGGREGATION_TYPE.SUCCESS,
          startDate: effectiveDateRange.start.toISOString(),
          endDate: effectiveDateRange.end.toISOString(),
          timezone,
          groupBy:
            config.groupBy === GROUP_BY_TYPE.ASSIGNEE
              ? GROUP_BY_TYPE.ASSIGNEE
              : undefined,
          filters: config.filters,
          metricFilter: config.metricFilter,
        }),
      )
    }

    if (isStringHumanReviewQueryEnabled) {
      queries.push(
        serializeDashboardBatchQuery({
          query_id: "string_human_review",
          kind: "queryHumanReviewMetric",
          type: "table",
          metricIds: stringMetricIds,
          metricOutputType: "string",
          aggregation: config.aggregation || AGGREGATION_TYPE.SUCCESS,
          startDate: effectiveDateRange.start.toISOString(),
          endDate: effectiveDateRange.end.toISOString(),
          timezone,
          groupBy:
            config.groupBy === GROUP_BY_TYPE.ASSIGNEE
              ? GROUP_BY_TYPE.ASSIGNEE
              : undefined,
          filters: config.filters,
          metricFilter: config.metricFilter,
        }),
      )
    }

    return { queries }
  }, [
    config.aggregation,
    config.filters,
    config.groupBy,
    config.groupByMetadataKey,
    config.metricFilter,
    effectiveDateRange.end,
    effectiveDateRange.start,
    floatMetricIds,
    isFloatDashboardQueryEnabled,
    isFloatHumanReviewQueryEnabled,
    isMonitoring,
    isStringDashboardQueryEnabled,
    isStringHumanReviewQueryEnabled,
    metricRecords,
    stringMetricIds,
    timezone,
  ])

  const dashboardBatchQueryEnabled = dashboardBatchRequest.queries.length > 0
  const dashboardBatchQuery = useDashboardBatchTransportQuery({
    request: dashboardBatchRequest,
    options: {
      enabled: dashboardBatchQueryEnabled,
      gcTime: CHART_QUERY_GC_TIME,
      staleTime: CHART_QUERY_STALE_TIME,
    },
  })

  const combinedRows = useMemo(() => {
    const results = dashboardBatchQuery.data?.results
    if (!results) {
      return []
    }

    return config.groupByMetadataKey && results.length > 1
      ? reconcileTableMetadataCohorts(results)
      : results.flatMap((result) => result.rows)
  }, [config.groupByMetadataKey, dashboardBatchQuery.data?.results])

  const uniqueAgentIds = useMemo(() => {
    const agentIds = combinedRows
      .map((row) =>
        typeof row.run_agent_id === "string" ? row.run_agent_id : null,
      )
      .filter((agentId): agentId is string => agentId != null)

    return Array.from(new Set(agentIds))
  }, [combinedRows])

  const { data: agentNames, isLoading: isAgentNamesLoading } =
    useAgentNamesTransport(uniqueAgentIds)

  const metricValues = useMemo(
    () =>
      normalizeTableMetricValues({
        agentNames,
        groupByMetadataKey: config.groupByMetadataKey,
        rows: combinedRows,
      }),
    [agentNames, combinedRows, config.groupByMetadataKey],
  )

  const isLoading =
    (hasMetrics && isMetricRecordsLoading) ||
    isTransportDashboardQueryLoading(
      dashboardBatchQuery,
      dashboardBatchQueryEnabled,
    ) ||
    isAgentNamesLoading

  const error = dashboardBatchQuery.error?.message ?? null

  return {
    dateRange: effectiveDateRange,
    error,
    hasMetrics,
    isGlobalOverrideActive: isOverrideActive,
    isLoading,
    metricRecords,
    metricValues,
  }
}
